import { RefObject, useEffect } from 'react'

const SPLIT_VIEW_TOP_VARIABLE = '--setting-split-view-top'
const SPLIT_VIEW_RIGHT_VARIABLE = '--setting-split-view-right'
/** The space the view leaves between its header and its first card, so the panel's top lines up with the cards. */
const GAP_BELOW_HEADER_PX = 30

/** Where the open panel sits: below the view's header and beside the page's scrollbar. */
export const SETTING_SPLIT_VIEW_POSITION = {
  top: `var(${SPLIT_VIEW_TOP_VARIABLE}, 0px)`,
  right: `var(${SPLIT_VIEW_RIGHT_VARIABLE}, 0px)`,
  height: `calc(100% - var(${SPLIT_VIEW_TOP_VARIABLE}, 0px))`,
}

type SplitViewPositionParams = {
  /** The view's header, which the panel sits under. */
  headerRef: RefObject<HTMLElement | null>
  /** Holds the position the panel reads; put it around the panel. */
  containerRef: RefObject<HTMLElement | null>
  enabled: boolean
}

/** The nearest ancestor that scrolls the page vertically, or null when the window does. */
function findPageScroller(element: HTMLElement): HTMLElement | null {
  for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
    if (/auto|scroll/.test(getComputedStyle(ancestor).overflowY)) {
      return ancestor
    }
  }
  return null
}

function getScrollbarWidth(scroller: HTMLElement | null): number {
  if (!scroller) {
    return 0
  }
  const { borderLeftWidth, borderRightWidth } = getComputedStyle(scroller)
  const borders = (Number.parseFloat(borderLeftWidth) || 0) + (Number.parseFloat(borderRightWidth) || 0)
  return Math.max(0, scroller.offsetWidth - scroller.clientWidth - borders)
}

/** Keeps the open split view below the view's header and clear of the page's scrollbar, as both move. */
export function useSplitViewPosition({ headerRef, containerRef, enabled }: SplitViewPositionParams): void {
  useEffect(() => {
    const header = headerRef.current
    const container = containerRef.current
    if (!enabled || !header || !container) {
      return
    }
    const pageScroller = findPageScroller(header)

    const followPage = (): void => {
      const panelTop = Math.max(0, Math.round(header.getBoundingClientRect().bottom) + GAP_BELOW_HEADER_PX)
      container.style.setProperty(SPLIT_VIEW_TOP_VARIABLE, `${panelTop}px`)
      container.style.setProperty(SPLIT_VIEW_RIGHT_VARIABLE, `${getScrollbarWidth(pageScroller)}px`)
    }

    followPage()
    document.addEventListener('scroll', followPage, { capture: true, passive: true })
    window.addEventListener('resize', followPage)
    const resizeObserver = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(followPage)
    resizeObserver?.observe(header)
    if (pageScroller) {
      resizeObserver?.observe(pageScroller)
    }

    return () => {
      document.removeEventListener('scroll', followPage, { capture: true })
      window.removeEventListener('resize', followPage)
      resizeObserver?.disconnect()
    }
  }, [headerRef, containerRef, enabled])
}
